<template>
  <div
    id="app"
    class="d-flex flex-column min-vh-100">
    <app-header />
    <main
      class="layout d-flex flex-grow-1"
      :class="[contentBackground, contentLayout]">
      <div class="back w-100">
        <app-progress />
        <div class="content pl-3 pr-3 pb-16 pt-xl-10 w-100">
          <vue-page-transition :name="currentTransition">
            <router-view />
          </vue-page-transition>
        </div>
      </div>
    </main>
    <app-footer />
    <app-drawer />
  </div>
</template>

<script>
import { eventBus } from './main'
import debounce from 'lodash/debounce'

export default {
  components: {

  },
  name: 'App',
  data() {
    return {
      urlUid: undefined,
      contentBackground: 'bg-light-light-5',
      contentLayout: 'layout--header',
    }
  },
  computed: {
    windowSize() {
      /// Returns current window width and height
      ///
      /// @example
      ///   {"width": 768, "height": 493 }
      ///   {"width": 1043, "height": 764 }
      ///   {"width": 1920, "height": 812 }

      return this.$store.state.config.windowSize
    },

    currentBreakpoint() {
      /// Returns current 'morphological' breakpoint value based on window width
      ///
      /// @example
      ///   'xs' | 'sm' | 'md' | 'lg' | 'xl'

      return this.$store.state.config.currentBreakpoint
    },

    windowScroll() {
      /// Returns current scroll position
      ///
      /// @link https://developer.mozilla.org/ru/docs/Web/API/Window/scrollY

      return this.$store.state.config.windowScroll
    },

    currentTransition() {
      return this.$store.state.config.currentTransition
    },

    localUid() {
      return localStorage.getItem('quiz_uid')
    },

    answers() {
      return this.$store.state.user.answers
    },

    answersLength() {
      return Object.keys(this.answers).length
    },

    questions() {
      return this.$store.state.user.questions
    },

    questionsLength() {
      return this.questions.length
    },

    questionsProgress() {
      return this.$store.state.user.questionsProgress
    },

    storedCredentials() {
      return {
        firstName: this.$store.state.user.firstName,
        lastName: this.$store.state.user.lastName,
      }
    },

    photoIsUploaded() {
      return this.$store.state.user.photoIsUploaded
    },

    dataReady() {
      return this.$store.state.user.dataReady
    },

    currentRoute() {
      return this.$route.name
    },
  },
  created() {
    this.initQuiz()
    this.getIngredients()
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    dataReady(newVal, oldVal) {
      if (newVal) this.removePreloader()
    },

    /* eslint-disable-next-line no-unused-vars */
    currentRoute(newVal, oldVal) {
      const self = this

      if (newVal == 'Questions') {
        setTimeout(() => {
          self.contentBackground = 'bg-white'
          self.contentLayout = 'layout--progressbar'
        }, 500)
      }
    },
  },
  mounted() {
    eventBus.$on('redirectUser', () => { this.redirectUser() })

    this.watchWindowSize()
    window.addEventListener('resize', debounce(this.watchWindowSize, 200))
    window.addEventListener('onorientationchange', this.watchWindowSize)
    window.addEventListener('scroll', this.watchWindowScroll)
  },
  methods: {
    initQuiz() {
      // Check at first if link has UID param
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      })
      let value = params.uid
      this.urlUid = value

      // If user come back with UID in url
      if (this.urlUid) {
        // Init Quiz with this url UID
        this.$store.dispatch('user/initQuiz', this.urlUid)
      } else {
        // If user come back without UID in url, but has localStorage UID
        if (this.localUid) {
          // Init Quiz with this local UID
          this.$store.dispatch('user/initQuiz', this.localUid)
        }
        // Else register new user and send him to Start page
        else {
          this.$store.dispatch('user/initQuiz')
        }
      }
    },

    getIngredients() {
      this.$store.dispatch('user/getIngredients')
    },

    redirectUser() {
      if (this.answersLength > 0 || this.photoIsUploaded) {
        this.$router.push('Questions')
      } else {
        if (this.storedCredentials.firstName && this.storedCredentials.lastName) {
          this.$router.push('Scanner')
        } else {
          this.$router.push('Start')
        }
      }
    },

    watchWindowSize() {
      const width = window.innerWidth
      const height = window.innerHeight

      this.$store.commit('config/SAVE_WINDOW_WIDTH', {
        width: width,
        height: height
      })

      this.$store.commit('config/SAVE_CURRENT_BREAKPOINT', width)
    },

    watchWindowScroll() {
      const scrollPos = window.scrollY
      this.$store.commit('config/SAVE_WINDOW_SCROLL', scrollPos)
    },

    removePreloader() {
      const preloader = document.querySelector('.preloader')

      // Show preloader at least half second to make it fancy :)
      setTimeout(() => {
        document.body.classList.remove('body--locked')
        preloader.classList.add('preloader--hidden')
        // This timeout should be synched with transition duration in public.html <style>
        setTimeout(() => { preloader.remove() }, 800)
      }, 500)
    },
  },
}
</script>

<style lang="scss">
// Core
@import '@/styles/nightvue';
@import '@/styles/animations/_animations';

@import "@/styles/components/_button";
@import "@/styles/components/_input";
@import "@/styles/components/_dropdown";
@import "@/styles/sections/_page";

:root {
  --overlay-bg: #57A4A0 !important;
}

.overlay-top,
.overlay-bottom {
  z-index: 11 !important;
  transition: transition(all, lazy, decelerated);
}
</style>
